import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMutating } from '@tanstack/react-query';
import type { Column, TicketSummary, User } from '@vd/types';
import { Button, KCard, EmptyState, cn } from '@vd/ui';
import { X, CheckCircle2 } from 'lucide-react';
import { TicketDetail } from '../ticket/TicketDetail';
import { useShortcutMap } from '../../lib/keyboard';
import { cardTime } from '../../lib/When';
import styles from './SingleView.module.css';
import { useOpenCardSession } from '../ticket/cardSessionContext';

export interface SingleViewProps {
  tickets: TicketSummary[];
  columns: Column[];
  creatorsById: Map<string, User>;
  membersById: Map<string, User>;
  /** Set of ticket ids currently being moved/mutated. Drives the
   * spinner overlay so the user sees progress when they change a
   * ticket's column from the embedded TicketDetail and the ticket
   * is about to leave the filtered queue. */
  movingTicketIds?: ReadonlySet<string>;
  /** Click handler for a sidebar-list ticket's column pill — opens
   * the same column-picker popover used by Cards/Table/Board views.
   * Wired from ProjectViewsPage's existing handleStatusClick. */
  onStatusClick?: (t: TicketSummary, anchor: HTMLElement) => void;
  onTypeClick?: (t: TicketSummary, anchor: HTMLElement) => void;
  /** A card's Open, Copy, ⋯ and assignee buttons, as on the other views. */
  onOpenSource?: (t: TicketSummary) => void;
  onCopyLink?: (t: TicketSummary) => void;
  onOverflowClick?: (t: TicketSummary, anchor: HTMLElement) => void;
  onAssignClick?: (t: TicketSummary, anchor: HTMLElement) => void;
  onClose?: () => void;
}

export function SingleView({
  tickets, columns, creatorsById, membersById, movingTicketIds, onStatusClick, onTypeClick,
  onOpenSource, onCopyLink, onOverflowClick, onAssignClick, onClose,
}: SingleViewProps) {
  const navigate = useNavigate();
  // If TicketDetail flipped us into Queue view via its "Queue view" button,
  // it left the ticket id in sessionStorage so we open on that ticket
  // instead of the first one in the list.
  const projectId = tickets[0]?.projectId;
  const openSession = useOpenCardSession();
  const [selected, setSelected] = useState<string | null>(() => {
    if (!projectId) return tickets[0]?.id ?? null;
    try {
      const stashed = sessionStorage.getItem(`vd-single-selected-${projectId}`);
      sessionStorage.removeItem(`vd-single-selected-${projectId}`);
      if (stashed && tickets.find((t) => t.id === stashed)) return stashed;
    } catch { /* noop */ }
    return tickets[0]?.id ?? null;
  });

  useEffect(() => {
    if (!selected || !tickets.find((t) => t.id === selected)) {
      setSelected(tickets[0]?.id ?? null);
    }
  }, [tickets, selected]);

  const moveBy = (delta: number) => {
    const i = tickets.findIndex((t) => t.id === selected);
    const next = tickets[Math.max(0, Math.min(tickets.length - 1, i + delta))];
    if (next) setSelected(next.id);
  };

  useShortcutMap({
    j: () => moveBy(1),
    k: () => moveBy(-1),
    ArrowDown: () => moveBy(1),
    ArrowUp: () => moveBy(-1),
    Enter: () => { if (selected) navigate(`/tickets/${selected}`); },
    Escape: () => onClose?.(),
  });

  // Hook order matters: useIsMutating MUST be called every render,
  // including when tickets is empty. Earlier this lived after an
  // early return for the empty case, which meant the hook was
  // skipped on empty renders — going from non-empty to empty caused
  // React error #310 ("Rendered fewer hooks than during the previous
  // render"). Triggered when the user emptied the queue by changing
  // the last ticket's column out of the filter.
  const detailMoveCount = useIsMutating({ mutationKey: ['move-ticket', selected] });
  const selectedIsMoving = !!selected && (movingTicketIds?.has(selected) || detailMoveCount > 0);

  const colById = new Map(columns.map((c) => [c.id, c]));

  // Two empty states the main pane has to handle:
  //   - tickets.length === 0: queue is empty (user finished triaging,
  //     or filter excludes everything). Show a celebratory all-done
  //     message rather than the alarming "no tickets" wording — this
  //     IS what success looks like in the queue-triage flow.
  //   - tickets.length > 0 but selected has no match: rare transient
  //     state during a column-change mutation. Falls back to the
  //     existing "Select a ticket on the right" prompt.
  // Either way we KEEP the shell layout (main + aside) instead of
  // replacing it entirely with EmptyState — replacing was the abrupt
  // unmount that caused the React error you'd hit when the last
  // ticket left the filter (TicketDetail unmounting mid-mutation).
  const queueIsEmpty = tickets.length === 0;

  return (
    <div className={styles.shell}>
      <div className={styles.main}>
        {queueIsEmpty ? (
          <EmptyState
            icon={<CheckCircle2 size={32} />}
            title="Queue cleared"
            description="No more tickets match your current filter. Adjust filters above, switch views, or come back when fresh tickets arrive."
          />
        ) : selected ? (
          <TicketDetail ticketId={selected} embedded />
        ) : (
          <div style={{ padding: 40 }}>Select a ticket on the right →</div>
        )}
        {selectedIsMoving && (
          // Overlay visible whenever the currently-selected ticket is
          // mid-move. Most common trigger: user filtered to a column,
          // is working through the queue, and changes a ticket's
          // column from the embedded TicketDetail. If the new column
          // isn't in the filter, the ticket disappears from the queue
          // when the mutation settles — the overlay tells the user
          // the move is in flight rather than letting the next
          // ticket appear silently. If the move stays in the filter,
          // the overlay clears when the mutation settles and the same
          // ticket stays selected.
          <div className={styles.movingOverlay} role="status" aria-live="polite" aria-label="Moving ticket">
            <div className={styles.movingCard}>
              <div className={styles.movingSpinner} aria-hidden />
              <div>Moving ticket…</div>
            </div>
          </div>
        )}
      </div>
      <aside className={styles.aside}>
        <div className={styles.asideHead}>
          <div>
            <div className={styles.asideTitle}>
              {tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'}
            </div>
            <div className={styles.asideSub}>
              {queueIsEmpty
                ? 'Adjust filters to see more.'
                : <>Click or <kbd>j</kbd>/<kbd>k</kbd> to navigate</>}
            </div>
          </div>
          {/* Close returns to the selected ticket's full detail page, not
              the board — the queue view is effectively a triage layer on
              top of a single ticket, so stepping out of it should land
              you on that ticket. Falls back to onClose (board) only when
              no ticket is selected. */}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<X size={14} />}
            onClick={() => {
              if (selected) navigate(`/tickets/${selected}`);
              else onClose?.();
            }}
          >
            Close
          </Button>
        </div>
        <div className={styles.list}>
          {tickets.map((t) => (
            <div key={t.id} onClick={() => setSelected(t.id)} className={cn(styles.item, selected === t.id && styles.itemSelected)}>
              <KCard
                ticket={t}
                creator={creatorsById.get(t.createdBy)}
                assignees={t.assignees.map((id) => membersById.get(id)).filter(Boolean) as User[]}
                column={colById.get(t.columnId)}
                showThumbnail={false}
                selected={selected === t.id}
                formatTime={cardTime}
                onStatusClick={onStatusClick}
                onTypeClick={onTypeClick}
                onOpenSource={onOpenSource}
                onCopyLink={onCopyLink}
                onSessionClick={openSession}
                onOverflowClick={onOverflowClick}
                onAssignClick={onAssignClick}
              />
            </div>
          ))}
        </div>
        {/* "Open in full view" footer removed — Close now does the same
            thing, and the duplication was confusing. */}
      </aside>
    </div>
  );
}
